import React, { FC, useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'

import useText from '../../hooks/useText'
import { useChargePointGetRemoteStartTagsQuery } from '../../redux/api/web-api'
import { Dialog } from '../../uikit/Dialog'
import { Dropdown } from '../../uikit/Dropdown'

const formId = 'select-rfid-tag-form'

export type SelectRfidTagDialogProps = {
    chargePointId: string
    connectorId: number
    visible: boolean
    onStartCharging: (rfidTag: string) => void
    onHide: () => void
}

export const SelectRfidTagDialog: FC<SelectRfidTagDialogProps> = ({
    visible,
    chargePointId,
    connectorId,
    onHide,
    onStartCharging,
}) => {
    const text = useText()
    const [selectedRfid, setSelectedRfid] = useState('')
    const { data: rfidTags = [], isLoading } = useChargePointGetRemoteStartTagsQuery({ chargePointId, connectorId })

    const rfidTagsOptions = useMemo(
        () =>
            rfidTags.map((tag) => ({
                value: tag.id || '',
                label: `${tag.chargePointUserId ? tag.fullName : tag.companyName} - ${tag.id}`,
            })),
        [rfidTags]
    )

    useEffect(() => {
        setSelectedRfid(rfidTagsOptions[0]?.value ?? '')
    }, [rfidTagsOptions])

    const startCharging = () => {
        if (!isLoading && selectedRfid) onStartCharging(selectedRfid)
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        startCharging()
    }

    const renderContent = () => {
        if (isLoading) return <div data-testid="shimmer-loading" className="shimmer-container shimmer-small" />
        if (rfidTagsOptions.length > 0)
            return (
                <Dropdown
                    id="rfid-tag-select"
                    label={text('lblSelectRFIDToStartCharging')}
                    value={selectedRfid}
                    options={rfidTagsOptions}
                    onChange={(event) => setSelectedRfid(event.value)}
                    showClear={false}
                />
            )

        return <p className="m-0 text-color-secondary">{text('msgNoRfidTagsAvailable')}</p>
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} data-testid="btn-cancel" />
            <Button
                label={text('btnStartCharging')}
                type="submit"
                form={formId}
                disabled={isLoading || rfidTagsOptions.length === 0}
                data-testid="btn-on-starting-charging"
            />
        </div>
    )

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={text('lblRemoteStartCharging')}
            footer={footer}
            data-testid="select-rfid-tag-dialog"
        >
            <form id={formId} className="flex flex-column gap-3" onSubmit={handleSubmit} noValidate>
                {renderContent()}
            </form>
        </Dialog>
    )
}
